import { supabase } from '../lib/supabase';

/**
 * exportCourseBackup
 * 
 * Exports all course structure and content data as a downloadable JSON file.
 * Uses the authenticated admin's session (RLS grants admin SELECT on all tables).
 * 
 * Includes: cohorts, modules, module_items, calendar_events, cohort_custom_events,
 * cohort_module_visibility, cohort_item_overrides, syllabus_pdfs, external_resources,
 * attendance_sessions, and settings.
 * 
 * Does NOT include: student PII, passwords, progress, submissions, messages, or notes
 * (unless includeStudentData is true).
 * 
 * @param {Object} options
 * @param {boolean} options.includeStudentData - Include profiles, progress, submissions, notes, percentiles
 * @param {Function} options.onProgress - Callback with { step, total, label } for progress updates
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export async function exportCourseBackup({ includeStudentData = false, onProgress } = {}) {
  const backup = {
    exportedAt: new Date().toISOString(),
    exportType: includeStudentData ? 'full' : 'course-structure',
    version: '1.0',
    tables: {}
  };

  // Define tables to export
  // Each entry: [tableName, query, label]
  const coreTables = [
    ['cohorts', () => supabase.from('cohorts').select('*').order('start_date'), 'Cohorts'],
    ['modules', () => supabase.from('modules').select('*').order('week_number'), 'Modules'],
    ['module_items', () => supabase.from('module_items').select('*').order('module_id, item_order'), 'Module Items'],
    ['calendar_events', () => supabase.from('calendar_events').select('*').order('week_number'), 'Calendar Events'],
    ['cohort_custom_events', () => supabase.from('cohort_custom_events').select('*').order('event_date'), 'Cohort Events'],
    ['cohort_module_visibility', () => supabase.from('cohort_module_visibility').select('*'), 'Module Visibility'],
    ['syllabus_pdfs', () => supabase.from('syllabus_pdfs').select('*'), 'Syllabus PDFs'],
  ];

  // These tables may or may not exist depending on migrations applied
  const optionalTables = [
    ['cohort_item_overrides', () => supabase.from('cohort_item_overrides').select('*'), 'Item Overrides'],
    ['external_resources', () => supabase.from('external_resources').select('*').order('display_order'), 'External Resources'],
    ['attendance_sessions', () => supabase.from('attendance_sessions').select('*').order('session_date'), 'Attendance Sessions'],
    ['settings', () => supabase.from('settings').select('*'), 'Settings'],
  ];

  const studentTables = includeStudentData ? [
    ['profiles', () => supabase.from('profiles').select('*').order('created_at'), 'Profiles'],
    ['student_progress', () => supabase.from('student_progress').select('*'), 'Student Progress'],
    ['student_notes', () => supabase.from('student_notes').select('*').order('created_at'), 'Student Notes'],
    ['student_percentiles', () => supabase.from('student_percentiles').select('*'), 'Student Percentiles'],
    ['assignment_submissions', () => supabase.from('assignment_submissions').select('*').order('submitted_at'), 'Submissions'],
    ['attendance_records', () => supabase.from('attendance_records').select('*'), 'Attendance Records'],
  ] : [];

  const allTables = [...coreTables, ...optionalTables, ...studentTables];
  const totalSteps = allTables.length;
  let currentStep = 0;

  for (const [tableName, queryFn, label] of allTables) {
    currentStep++;
    if (onProgress) {
      onProgress({ step: currentStep, total: totalSteps, label });
    }

    try {
      const { data, error } = await queryFn();
      if (error) {
        // Table might not exist - skip silently for optional tables
        const isOptional = optionalTables.some(([name]) => name === tableName) 
                        || studentTables.some(([name]) => name === tableName);
        if (isOptional) {
          console.log(`Skipping ${tableName} (not found or no access)`);
          continue;
        }
        // Core table error - still continue but note it
        console.warn(`Error exporting ${tableName}:`, error.message);
        backup.tables[tableName] = { error: error.message, rows: 0 };
        continue;
      }
      backup.tables[tableName] = {
        rows: (data || []).length,
        data: data || []
      };
    } catch (err) {
      console.warn(`Exception exporting ${tableName}:`, err.message);
      // Skip - table might not exist
    }
  }

  // Summary
  backup.summary = {};
  for (const [tableName] of allTables) {
    if (backup.tables[tableName]) {
      backup.summary[tableName] = backup.tables[tableName].rows || 0;
    }
  }

  // Download as JSON file
  try {
    const json = JSON.stringify(backup, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const now = new Date();
    const timestamp = now.toISOString().slice(0, 10); // YYYY-MM-DD
    const filename = `codebreakers-backup-${timestamp}.json`;

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true, filename, summary: backup.summary };
  } catch (err) {
    console.error('Error creating backup file:', err);
    return { success: false, error: err.message || 'Failed to create backup file' };
  }
}
